/** @format */

import { useState, useEffect, useCallback } from "react";
import { collection, getDocs, query, limit, where } from "firebase/firestore";
import { db } from "../firebase/config";

const useBookStats = () => {
  const [bookUserCounts, setBookUserCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetchTime, setLastFetchTime] = useState(0);

  // Cache duration in milliseconds (10 minutes for better performance)
  const CACHE_DURATION = 10 * 60 * 1000;

  // In-memory cache with TTL
  const [cache, setCache] = useState(new Map());
  const [cacheExpiry, setCacheExpiry] = useState(new Map());

  // Check if cache is valid
  const isCacheValid = useCallback(
    (key) => {
      const expiry = cacheExpiry.get(key);
      return expiry && Date.now() < expiry;
    },
    [cacheExpiry]
  );

  // Get from cache
  const getFromCache = useCallback(
    (key) => {
      if (isCacheValid(key)) {
        return cache.get(key);
      }
      return null;
    },
    [cache, isCacheValid]
  );

  // Set cache with TTL
  const setCacheWithTTL = useCallback(
    (key, value, ttlMs = CACHE_DURATION) => {
      const expiry = Date.now() + ttlMs;
      cache.set(key, value);
      cacheExpiry.set(key, expiry);
      setCache(new Map(cache));
      setCacheExpiry(new Map(cacheExpiry));
    },
    [cache, cacheExpiry, CACHE_DURATION]
  );

  // Get user counts for all books efficiently with smart caching and batching
  const fetchBookUserCounts = useCallback(
    async (forceRefresh = false) => {
      // Check if we can use cached data
      const now = Date.now();
      if (
        !forceRefresh &&
        now - lastFetchTime < CACHE_DURATION &&
        Object.keys(bookUserCounts).length > 0
      ) {
        console.log("Using cached book user counts");
        return;
      }

      // Check in-memory cache first
      const cacheKey = "bookUserCounts";
      const cachedData = getFromCache(cacheKey);
      if (!forceRefresh && cachedData) {
        console.log("Using in-memory cached book user counts");
        setBookUserCounts(cachedData);
        setLastFetchTime(now);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        console.log("Fetching book user counts...");
        const startTime = performance.now();

        // Strategy 1: Try to get Mybooks documents with limits (more efficient)
        try {
          const mybooksQuery = query(
            collection(db, "Mybooks"),
            limit(100) // Limit to 100 users instead of ALL
          );

          const mybooksSnapshot = await getDocs(mybooksQuery);
          const mybooksDocs = mybooksSnapshot.docs;

          if (mybooksDocs.length > 0) {
            console.log(
              `Found ${mybooksDocs.length} Mybooks documents, processing...`
            );

            const counts = {};
            const batchSize = 10; // Smaller batch size to be more respectful
            const batches = [];

            // Create batches of Mybooks documents
            for (let i = 0; i < mybooksDocs.length; i += batchSize) {
              batches.push(mybooksDocs.slice(i, i + batchSize));
            }

            // Process batches with concurrent subcollection queries
            for (let i = 0; i < batches.length; i++) {
              const currentBatch = batches[i];
              console.log(
                `Processing Mybooks batch ${i + 1}/${batches.length} (${
                  currentBatch.length
                } users)`
              );

              // Process all users in current batch concurrently
              const batchPromises = currentBatch.map(async (userDoc) => {
                try {
                  const userBooksRef = collection(
                    db,
                    "Mybooks",
                    userDoc.id,
                    "books"
                  );
                  const userBooksSnapshot = await getDocs(userBooksRef);

                  return userBooksSnapshot.docs
                    .map((bookDoc) => {
                      const bookData = bookDoc.data();
                      return bookData.bookid;
                    })
                    .filter((bookId) => bookId);
                } catch (err) {
                  // User might not have a books subcollection yet
                  return [];
                }
              });

              // Wait for current batch to complete
              const batchResults = await Promise.all(batchPromises);

              // Aggregate results from current batch
              batchResults.forEach((bookIds) => {
                bookIds.forEach((bookId) => {
                  counts[bookId] = (counts[bookId] || 0) + 1;
                });
              });

              // Small delay between batches to be respectful to Firestore
              if (i < batches.length - 1) {
                await new Promise((resolve) => setTimeout(resolve, 100)); // Increased delay
              }
            }

            const endTime = performance.now();
            console.log(
              `Book user counts fetched in ${(endTime - startTime).toFixed(
                2
              )}ms`
            );
            console.log(
              `Found ${
                Object.keys(counts).length
              } unique books with assignments`
            );

            // Cache the results
            setCacheWithTTL(cacheKey, counts);
            setBookUserCounts(counts);
            setLastFetchTime(now);
            return;
          }
        } catch (err) {
          console.log(
            "Strategy 1 failed, falling back to Strategy 2:",
            err.message
          );
        }

        // Strategy 2: Fallback to users collection approach with limits
        console.log("Using fallback strategy...");
        const usersQuery = query(
          collection(db, "users"),
          where("status", "==", "active"), // Only get active users
          limit(100) // Limit to 100 users instead of ALL
        );

        const usersSnapshot = await getDocs(usersQuery);
        const users = usersSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        console.log(`Found ${users.length} users, fetching their books...`);

        const counts = {};
        const batchSize = 10; // Reduced batch size for better performance
        const batches = [];

        // Create batches of users
        for (let i = 0; i < users.length; i += batchSize) {
          batches.push(users.slice(i, i + batchSize));
        }

        // Process batches sequentially to avoid overwhelming Firestore
        for (let i = 0; i < batches.length; i++) {
          const batch = batches[i];
          console.log(
            `Processing users batch ${i + 1}/${batches.length} (${
              batch.length
            } users)`
          );

          // Process users in current batch concurrently
          const batchPromises = batch.map(async (user) => {
            try {
              const userBooksRef = collection(db, "Mybooks", user.id, "books");
              const userBooksSnapshot = await getDocs(userBooksRef);

              return userBooksSnapshot.docs
                .map((bookDoc) => {
                  const bookData = bookDoc.data();
                  return bookData.bookid;
                })
                .filter((bookId) => bookId);
            } catch (err) {
              return [];
            }
          });

          // Wait for current batch to complete
          const batchResults = await Promise.all(batchPromises);

          // Aggregate results from current batch
          batchResults.forEach((bookIds) => {
            bookIds.forEach((bookId) => {
              counts[bookId] = (counts[bookId] || 0) + 1;
            });
          });

          // Small delay between batches
          if (i < batches.length - 1) {
            await new Promise((resolve) => setTimeout(resolve, 100));
          }
        }

        const endTime = performance.now();
        console.log(
          `Book user counts fetched in ${(endTime - startTime).toFixed(2)}ms`
        );
        console.log(
          `Found ${Object.keys(counts).length} unique books with assignments`
        );

        // Cache the results
        setCacheWithTTL(cacheKey, counts);
        setBookUserCounts(counts);
        setLastFetchTime(now);
      } catch (err) {
        console.error("Error fetching book user counts:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    },
    [
      bookUserCounts,
      lastFetchTime,
      CACHE_DURATION,
      getFromCache,
      setCacheWithTTL,
    ]
  );

  // Get user count for a specific book
  const getUserCountForBook = useCallback(
    (bookId) => {
      return bookUserCounts[bookId] || 0;
    },
    [bookUserCounts]
  );

  // Refresh the counts (force refresh)
  const refreshCounts = useCallback(() => {
    fetchBookUserCounts(true);
  }, [fetchBookUserCounts]);

  // Get counts for specific books only (more efficient for individual book queries)
  const getBookUserCounts = useCallback(
    (bookIds) => {
      if (!Array.isArray(bookIds)) {
        bookIds = [bookIds];
      }

      const result = {};
      bookIds.forEach((bookId) => {
        result[bookId] = bookUserCounts[bookId] || 0;
      });

      return result;
    },
    [bookUserCounts]
  );

  // Clear cache manually if needed
  const clearCache = useCallback(() => {
    setCache(new Map());
    setCacheExpiry(new Map());
    setLastFetchTime(0);
  }, []);

  useEffect(() => {
    fetchBookUserCounts();
  }, [fetchBookUserCounts]);

  return {
    bookUserCounts,
    loading,
    error,
    getUserCountForBook,
    getBookUserCounts,
    refreshCounts,
    clearCache,
    lastFetchTime,
  };
};

export default useBookStats;
